<template>
  <div class="w-full">
    <div ref="editorRoot" class="min-h-[400px]"></div>
  </div>
</template>

<script>
import Editor from '@toast-ui/editor'
import '@toast-ui/editor/dist/toastui-editor.css'

export default {
  name: 'UIMarkdownEditor',
  props: {
    modelValue: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      editor: null,
    }
  },
  mounted() {
    this.initEditor()
  },
  beforeUnmount() {
    this.destroyEditor()
  },
  methods: {
    initEditor() {
      try {
        if (!this.$refs.editorRoot) return

        this.editor = new Editor({
          el: this.$refs.editorRoot,
          height: '100px',
          initialEditType: 'markdown',
          previewStyle: 'tab',
          hideModeSwitch: true,
          initialValue: this.modelValue || '',
          toolbarItems: [
            ['heading', 'bold', 'italic', 'strike'],
            ['hr', 'quote'],
            ['ul', 'ol', 'task'],
            ['table', 'link'],
            ['code', 'codeblock'],
          ],
          events: {
            change: () => {
              const markdown = this.editor.getMarkdown()
              this.$emit('update:modelValue', markdown)
            },
          },
        })
      } catch (error) {}
    },
    destroyEditor() {
      if (this.editor && typeof this.editor.destroy === 'function') {
        this.editor.destroy()
      }
    },
    getMarkdown() {
      if (this.editor) {
        return this.editor.getMarkdown()
      }
      return ''
    },
    getHTML() {
      if (this.editor) {
        return this.editor.getHTML()
      }
      return ''
    },
    setMarkdown(markdown) {
      if (this.editor) {
        this.editor.setMarkdown(markdown)
      }
    },
  },
}
</script>

<style scoped>
/* Modern, clean Toast UI Editor styling */
:deep(.toastui-editor) {
  border: none !important;
  width: 100%;
  box-shadow: none !important;
}

:deep(.toastui-editor .toastui-editor-defaultUI) {
  border: none !important;
  border-radius: 12px;
  width: 100%;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

/* Modern toolbar styling */
:deep(.toastui-editor .toastui-editor-toolbar) {
  border: none !important;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 12px 16px;
  border-radius: 12px 12px 0 0;
}

:deep(.toastui-editor .toastui-editor-toolbar .toastui-editor-toolbar-group) {
  border: none !important;
  margin-right: 8px;
}

:deep(.toastui-editor .toastui-editor-toolbar button) {
  color: white !important;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 8px 10px;
  margin: 0 2px;
  transition: all 0.2s ease;
  font-weight: 500;
}

:deep(.toastui-editor .toastui-editor-toolbar button:hover) {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
}

:deep(.toastui-editor .toastui-editor-toolbar button.active) {
  background: rgba(255, 255, 255, 0.9);
  color: #667eea !important;
  border-color: rgba(255, 255, 255, 0.9);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Modern editor content area */
:deep(.toastui-editor .toastui-editor-md-container) {
  width: 100% !important;
  background: #ffffff;
}

:deep(.toastui-editor .toastui-editor-md-container .toastui-editor) {
  width: 100% !important;
  border: none !important;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 15px;
  line-height: 1.6;
  padding: 20px;
  background: #ffffff;
}

:deep(.toastui-editor .toastui-editor-md-container .toastui-editor:focus) {
  outline: none;
  box-shadow: inset 0 0 0 2px #667eea;
  border-radius: 0 0 12px 12px;
}

/* Allow tabs + preview (re-enable default layout) */
/* We keep our modern styling above, but remove hard hides. */

/* Custom scrollbar */
:deep(.toastui-editor .toastui-editor-md-container .toastui-editor::-webkit-scrollbar) {
  width: 6px;
}

:deep(.toastui-editor .toastui-editor-md-container .toastui-editor::-webkit-scrollbar-track) {
  background: #f1f5f9;
  border-radius: 3px;
}

:deep(.toastui-editor .toastui-editor-md-container .toastui-editor::-webkit-scrollbar-thumb) {
  background: #cbd5e1;
  border-radius: 3px;
}

:deep(.toastui-editor .toastui-editor-md-container .toastui-editor::-webkit-scrollbar-thumb:hover) {
  background: #94a3b8;
}
</style>
